/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * Builds messages and fields from the generated profile.
 *
 * The decoder only ever sees numbers, so this is where a global message number
 * turns back into names, base types, scales and components.
 *
 * Each message type's field metadata lives in its own generated class and is
 * built the first time that class is touched, so a file that contains three
 * message types costs three prototypes rather than all 123.
 */
public object Factory {
    /** A message carrying every field the profile declares for [mesgNum], with no values. */
    public fun createMesg(mesgNum: UShort): Mesg {
        val prototype = prototypeFields(mesgNum) ?: return createDefaultMesg(mesgNum)
        val mesg = Mesg(mesgName(mesgNum), mesgNum)
        for (field in prototype.values) mesg.setField(Field(field))
        return mesg
    }

    /**
     * The profile definition of one field, or null when the profile declares no
     * such field on that message.
     */
    public fun createField(mesgNum: UShort, fieldNum: UByte): Field? =
        prototypeFields(mesgNum)?.get(fieldNum.toInt())?.let { Field(it) }

    /** A message for a global number the profile does not know. */
    public fun createDefaultMesg(mesgNum: UShort): Mesg = Mesg(UNKNOWN_NAME, mesgNum)

    /** A field for a number the profile does not know, typed from the file itself. */
    public fun createDefaultField(fieldNum: UByte, baseType: BaseType): Field =
        Field(fieldName = UNKNOWN_NAME, fieldNum = fieldNum, baseType = baseType)

    /** True when the profile knows this global message number. */
    public fun isKnownMesg(mesgNum: UShort): Boolean = prototypeFields(mesgNum) != null

    internal const val UNKNOWN_NAME: String = "unknown"

    internal fun mesgName(mesgNum: UShort): String =
        when (mesgNum.toInt()) {
            0 -> "FileId"
            49 -> "FileCreator"
            162 -> "TimestampCorrelation"
            35 -> "Software"
            106 -> "SlaveDevice"
            1 -> "Capabilities"
            37 -> "FileCapabilities"
            38 -> "MesgCapabilities"
            39 -> "FieldCapabilities"
            2 -> "DeviceSettings"
            3 -> "UserProfile"
            4 -> "HrmProfile"
            5 -> "SdmProfile"
            6 -> "BikeProfile"
            127 -> "Connectivity"
            159 -> "WatchfaceSettings"
            188 -> "OhrSettings"
            216 -> "TimeInZone"
            7 -> "ZonesTarget"
            12 -> "Sport"
            8 -> "HrZone"
            53 -> "SpeedZone"
            131 -> "CadenceZone"
            9 -> "PowerZone"
            10 -> "MetZone"
            13 -> "TrainingSettings"
            258 -> "DiveSettings"
            262 -> "DiveAlarm"
            393 -> "DiveApneaAlarm"
            259 -> "DiveGas"
            15 -> "Goal"
            34 -> "Activity"
            18 -> "Session"
            19 -> "Lap"
            101 -> "Length"
            20 -> "Record"
            21 -> "Event"
            23 -> "DeviceInfo"
            375 -> "DeviceAuxBatteryInfo"
            72 -> "TrainingFile"
            104 -> "Battery"
            128 -> "WeatherConditions"
            129 -> "WeatherAlert"
            160 -> "GpsMetadata"
            161 -> "CameraEvent"
            164 -> "GyroscopeData"
            165 -> "AccelerometerData"
            208 -> "MagnetometerData"
            209 -> "BarometerData"
            167 -> "ThreeDSensorCalibration"
            210 -> "OneDSensorCalibration"
            169 -> "VideoFrame"
            174 -> "ObdiiData"
            177 -> "NmeaSentence"
            178 -> "AviationAttitude"
            184 -> "Video"
            185 -> "VideoTitle"
            186 -> "VideoDescription"
            187 -> "VideoClip"
            225 -> "Set"
            285 -> "Jump"
            312 -> "Split"
            313 -> "SplitSummary"
            317 -> "ClimbPro"
            206 -> "FieldDescription"
            207 -> "DeveloperDataId"
            31 -> "Course"
            32 -> "CoursePoint"
            148 -> "SegmentId"
            149 -> "SegmentLeaderboardEntry"
            150 -> "SegmentPoint"
            142 -> "SegmentLap"
            151 -> "SegmentFile"
            26 -> "Workout"
            158 -> "WorkoutSession"
            27 -> "WorkoutStep"
            264 -> "ExerciseTitle"
            28 -> "Schedule"
            33 -> "Totals"
            30 -> "WeightScale"
            51 -> "BloodPressure"
            103 -> "MonitoringInfo"
            55 -> "Monitoring"
            211 -> "MonitoringHrData"
            269 -> "Spo2Data"
            132 -> "Hr"
            227 -> "StressLevel"
            229 -> "MaxMetData"
            314 -> "HsaBodyBatteryData"
            315 -> "HsaEvent"
            302 -> "HsaAccelerometerData"
            376 -> "HsaGyroscopeData"
            304 -> "HsaStepData"
            305 -> "HsaSpo2Data"
            306 -> "HsaStressData"
            307 -> "HsaRespirationData"
            308 -> "HsaHeartRateData"
            389 -> "HsaConfigurationData"
            409 -> "HsaWristTemperatureData"
            145 -> "MemoGlob"
            275 -> "SleepLevel"
            82 -> "AntChannelId"
            80 -> "AntRx"
            81 -> "AntTx"
            200 -> "ExdScreenConfiguration"
            201 -> "ExdDataFieldConfiguration"
            202 -> "ExdDataConceptConfiguration"
            268 -> "DiveSummary"
            289 -> "AadAccelFeatures"
            78 -> "Hrv"
            290 -> "BeatIntervals"
            370 -> "HrvStatusSummary"
            371 -> "HrvValue"
            372 -> "RawBbi"
            297 -> "RespirationRate"
            387 -> "ChronoShotSession"
            388 -> "ChronoShotData"
            319 -> "TankUpdate"
            323 -> "TankSummary"
            346 -> "SleepAssessment"
            470 -> "SleepDisruptionSeverityPeriod"
            471 -> "SleepDisruptionOvernightSeverity"
            412 -> "NapEvent"
            398 -> "SkinTempOvernight"
            105 -> "Pad"
            else -> UNKNOWN_NAME
        }

    internal fun prototypeFields(mesgNum: UShort): Map<Int, Field>? =
        when (mesgNum.toInt()) {
            0 -> FileIdMesg.PROTOTYPE_FIELDS
            49 -> FileCreatorMesg.PROTOTYPE_FIELDS
            162 -> TimestampCorrelationMesg.PROTOTYPE_FIELDS
            35 -> SoftwareMesg.PROTOTYPE_FIELDS
            106 -> SlaveDeviceMesg.PROTOTYPE_FIELDS
            1 -> CapabilitiesMesg.PROTOTYPE_FIELDS
            37 -> FileCapabilitiesMesg.PROTOTYPE_FIELDS
            38 -> MesgCapabilitiesMesg.PROTOTYPE_FIELDS
            39 -> FieldCapabilitiesMesg.PROTOTYPE_FIELDS
            2 -> DeviceSettingsMesg.PROTOTYPE_FIELDS
            3 -> UserProfileMesg.PROTOTYPE_FIELDS
            4 -> HrmProfileMesg.PROTOTYPE_FIELDS
            5 -> SdmProfileMesg.PROTOTYPE_FIELDS
            6 -> BikeProfileMesg.PROTOTYPE_FIELDS
            127 -> ConnectivityMesg.PROTOTYPE_FIELDS
            159 -> WatchfaceSettingsMesg.PROTOTYPE_FIELDS
            188 -> OhrSettingsMesg.PROTOTYPE_FIELDS
            216 -> TimeInZoneMesg.PROTOTYPE_FIELDS
            7 -> ZonesTargetMesg.PROTOTYPE_FIELDS
            12 -> SportMesg.PROTOTYPE_FIELDS
            8 -> HrZoneMesg.PROTOTYPE_FIELDS
            53 -> SpeedZoneMesg.PROTOTYPE_FIELDS
            131 -> CadenceZoneMesg.PROTOTYPE_FIELDS
            9 -> PowerZoneMesg.PROTOTYPE_FIELDS
            10 -> MetZoneMesg.PROTOTYPE_FIELDS
            13 -> TrainingSettingsMesg.PROTOTYPE_FIELDS
            258 -> DiveSettingsMesg.PROTOTYPE_FIELDS
            262 -> DiveAlarmMesg.PROTOTYPE_FIELDS
            393 -> DiveApneaAlarmMesg.PROTOTYPE_FIELDS
            259 -> DiveGasMesg.PROTOTYPE_FIELDS
            15 -> GoalMesg.PROTOTYPE_FIELDS
            34 -> ActivityMesg.PROTOTYPE_FIELDS
            18 -> SessionMesg.PROTOTYPE_FIELDS
            19 -> LapMesg.PROTOTYPE_FIELDS
            101 -> LengthMesg.PROTOTYPE_FIELDS
            20 -> RecordMesg.PROTOTYPE_FIELDS
            21 -> EventMesg.PROTOTYPE_FIELDS
            23 -> DeviceInfoMesg.PROTOTYPE_FIELDS
            375 -> DeviceAuxBatteryInfoMesg.PROTOTYPE_FIELDS
            72 -> TrainingFileMesg.PROTOTYPE_FIELDS
            104 -> BatteryMesg.PROTOTYPE_FIELDS
            128 -> WeatherConditionsMesg.PROTOTYPE_FIELDS
            129 -> WeatherAlertMesg.PROTOTYPE_FIELDS
            160 -> GpsMetadataMesg.PROTOTYPE_FIELDS
            161 -> CameraEventMesg.PROTOTYPE_FIELDS
            164 -> GyroscopeDataMesg.PROTOTYPE_FIELDS
            165 -> AccelerometerDataMesg.PROTOTYPE_FIELDS
            208 -> MagnetometerDataMesg.PROTOTYPE_FIELDS
            209 -> BarometerDataMesg.PROTOTYPE_FIELDS
            167 -> ThreeDSensorCalibrationMesg.PROTOTYPE_FIELDS
            210 -> OneDSensorCalibrationMesg.PROTOTYPE_FIELDS
            169 -> VideoFrameMesg.PROTOTYPE_FIELDS
            174 -> ObdiiDataMesg.PROTOTYPE_FIELDS
            177 -> NmeaSentenceMesg.PROTOTYPE_FIELDS
            178 -> AviationAttitudeMesg.PROTOTYPE_FIELDS
            184 -> VideoMesg.PROTOTYPE_FIELDS
            185 -> VideoTitleMesg.PROTOTYPE_FIELDS
            186 -> VideoDescriptionMesg.PROTOTYPE_FIELDS
            187 -> VideoClipMesg.PROTOTYPE_FIELDS
            225 -> SetMesg.PROTOTYPE_FIELDS
            285 -> JumpMesg.PROTOTYPE_FIELDS
            312 -> SplitMesg.PROTOTYPE_FIELDS
            313 -> SplitSummaryMesg.PROTOTYPE_FIELDS
            317 -> ClimbProMesg.PROTOTYPE_FIELDS
            206 -> FieldDescriptionMesg.PROTOTYPE_FIELDS
            207 -> DeveloperDataIdMesg.PROTOTYPE_FIELDS
            31 -> CourseMesg.PROTOTYPE_FIELDS
            32 -> CoursePointMesg.PROTOTYPE_FIELDS
            148 -> SegmentIdMesg.PROTOTYPE_FIELDS
            149 -> SegmentLeaderboardEntryMesg.PROTOTYPE_FIELDS
            150 -> SegmentPointMesg.PROTOTYPE_FIELDS
            142 -> SegmentLapMesg.PROTOTYPE_FIELDS
            151 -> SegmentFileMesg.PROTOTYPE_FIELDS
            26 -> WorkoutMesg.PROTOTYPE_FIELDS
            158 -> WorkoutSessionMesg.PROTOTYPE_FIELDS
            27 -> WorkoutStepMesg.PROTOTYPE_FIELDS
            264 -> ExerciseTitleMesg.PROTOTYPE_FIELDS
            28 -> ScheduleMesg.PROTOTYPE_FIELDS
            33 -> TotalsMesg.PROTOTYPE_FIELDS
            30 -> WeightScaleMesg.PROTOTYPE_FIELDS
            51 -> BloodPressureMesg.PROTOTYPE_FIELDS
            103 -> MonitoringInfoMesg.PROTOTYPE_FIELDS
            55 -> MonitoringMesg.PROTOTYPE_FIELDS
            211 -> MonitoringHrDataMesg.PROTOTYPE_FIELDS
            269 -> Spo2DataMesg.PROTOTYPE_FIELDS
            132 -> HrMesg.PROTOTYPE_FIELDS
            227 -> StressLevelMesg.PROTOTYPE_FIELDS
            229 -> MaxMetDataMesg.PROTOTYPE_FIELDS
            314 -> HsaBodyBatteryDataMesg.PROTOTYPE_FIELDS
            315 -> HsaEventMesg.PROTOTYPE_FIELDS
            302 -> HsaAccelerometerDataMesg.PROTOTYPE_FIELDS
            376 -> HsaGyroscopeDataMesg.PROTOTYPE_FIELDS
            304 -> HsaStepDataMesg.PROTOTYPE_FIELDS
            305 -> HsaSpo2DataMesg.PROTOTYPE_FIELDS
            306 -> HsaStressDataMesg.PROTOTYPE_FIELDS
            307 -> HsaRespirationDataMesg.PROTOTYPE_FIELDS
            308 -> HsaHeartRateDataMesg.PROTOTYPE_FIELDS
            389 -> HsaConfigurationDataMesg.PROTOTYPE_FIELDS
            409 -> HsaWristTemperatureDataMesg.PROTOTYPE_FIELDS
            145 -> MemoGlobMesg.PROTOTYPE_FIELDS
            275 -> SleepLevelMesg.PROTOTYPE_FIELDS
            82 -> AntChannelIdMesg.PROTOTYPE_FIELDS
            80 -> AntRxMesg.PROTOTYPE_FIELDS
            81 -> AntTxMesg.PROTOTYPE_FIELDS
            200 -> ExdScreenConfigurationMesg.PROTOTYPE_FIELDS
            201 -> ExdDataFieldConfigurationMesg.PROTOTYPE_FIELDS
            202 -> ExdDataConceptConfigurationMesg.PROTOTYPE_FIELDS
            268 -> DiveSummaryMesg.PROTOTYPE_FIELDS
            289 -> AadAccelFeaturesMesg.PROTOTYPE_FIELDS
            78 -> HrvMesg.PROTOTYPE_FIELDS
            290 -> BeatIntervalsMesg.PROTOTYPE_FIELDS
            370 -> HrvStatusSummaryMesg.PROTOTYPE_FIELDS
            371 -> HrvValueMesg.PROTOTYPE_FIELDS
            372 -> RawBbiMesg.PROTOTYPE_FIELDS
            297 -> RespirationRateMesg.PROTOTYPE_FIELDS
            387 -> ChronoShotSessionMesg.PROTOTYPE_FIELDS
            388 -> ChronoShotDataMesg.PROTOTYPE_FIELDS
            319 -> TankUpdateMesg.PROTOTYPE_FIELDS
            323 -> TankSummaryMesg.PROTOTYPE_FIELDS
            346 -> SleepAssessmentMesg.PROTOTYPE_FIELDS
            470 -> SleepDisruptionSeverityPeriodMesg.PROTOTYPE_FIELDS
            471 -> SleepDisruptionOvernightSeverityMesg.PROTOTYPE_FIELDS
            412 -> NapEventMesg.PROTOTYPE_FIELDS
            398 -> SkinTempOvernightMesg.PROTOTYPE_FIELDS
            105 -> PadMesg.PROTOTYPE_FIELDS
            else -> null
        }

    /**
     * Wraps [mesg] in the generated class for its message number, when there is
     * one — adopting its fields, not copying them. The caller must own [mesg]
     * outright and discard it, as [Mesg]'s adoption constructor requires.
     */
    internal fun typed(mesg: Mesg): Mesg =
        when (mesg.globalMesgNum.toInt()) {
            0 -> FileIdMesg(mesg, adopt = true)
            49 -> FileCreatorMesg(mesg, adopt = true)
            162 -> TimestampCorrelationMesg(mesg, adopt = true)
            35 -> SoftwareMesg(mesg, adopt = true)
            106 -> SlaveDeviceMesg(mesg, adopt = true)
            1 -> CapabilitiesMesg(mesg, adopt = true)
            37 -> FileCapabilitiesMesg(mesg, adopt = true)
            38 -> MesgCapabilitiesMesg(mesg, adopt = true)
            39 -> FieldCapabilitiesMesg(mesg, adopt = true)
            2 -> DeviceSettingsMesg(mesg, adopt = true)
            3 -> UserProfileMesg(mesg, adopt = true)
            4 -> HrmProfileMesg(mesg, adopt = true)
            5 -> SdmProfileMesg(mesg, adopt = true)
            6 -> BikeProfileMesg(mesg, adopt = true)
            127 -> ConnectivityMesg(mesg, adopt = true)
            159 -> WatchfaceSettingsMesg(mesg, adopt = true)
            188 -> OhrSettingsMesg(mesg, adopt = true)
            216 -> TimeInZoneMesg(mesg, adopt = true)
            7 -> ZonesTargetMesg(mesg, adopt = true)
            12 -> SportMesg(mesg, adopt = true)
            8 -> HrZoneMesg(mesg, adopt = true)
            53 -> SpeedZoneMesg(mesg, adopt = true)
            131 -> CadenceZoneMesg(mesg, adopt = true)
            9 -> PowerZoneMesg(mesg, adopt = true)
            10 -> MetZoneMesg(mesg, adopt = true)
            13 -> TrainingSettingsMesg(mesg, adopt = true)
            258 -> DiveSettingsMesg(mesg, adopt = true)
            262 -> DiveAlarmMesg(mesg, adopt = true)
            393 -> DiveApneaAlarmMesg(mesg, adopt = true)
            259 -> DiveGasMesg(mesg, adopt = true)
            15 -> GoalMesg(mesg, adopt = true)
            34 -> ActivityMesg(mesg, adopt = true)
            18 -> SessionMesg(mesg, adopt = true)
            19 -> LapMesg(mesg, adopt = true)
            101 -> LengthMesg(mesg, adopt = true)
            20 -> RecordMesg(mesg, adopt = true)
            21 -> EventMesg(mesg, adopt = true)
            23 -> DeviceInfoMesg(mesg, adopt = true)
            375 -> DeviceAuxBatteryInfoMesg(mesg, adopt = true)
            72 -> TrainingFileMesg(mesg, adopt = true)
            104 -> BatteryMesg(mesg, adopt = true)
            128 -> WeatherConditionsMesg(mesg, adopt = true)
            129 -> WeatherAlertMesg(mesg, adopt = true)
            160 -> GpsMetadataMesg(mesg, adopt = true)
            161 -> CameraEventMesg(mesg, adopt = true)
            164 -> GyroscopeDataMesg(mesg, adopt = true)
            165 -> AccelerometerDataMesg(mesg, adopt = true)
            208 -> MagnetometerDataMesg(mesg, adopt = true)
            209 -> BarometerDataMesg(mesg, adopt = true)
            167 -> ThreeDSensorCalibrationMesg(mesg, adopt = true)
            210 -> OneDSensorCalibrationMesg(mesg, adopt = true)
            169 -> VideoFrameMesg(mesg, adopt = true)
            174 -> ObdiiDataMesg(mesg, adopt = true)
            177 -> NmeaSentenceMesg(mesg, adopt = true)
            178 -> AviationAttitudeMesg(mesg, adopt = true)
            184 -> VideoMesg(mesg, adopt = true)
            185 -> VideoTitleMesg(mesg, adopt = true)
            186 -> VideoDescriptionMesg(mesg, adopt = true)
            187 -> VideoClipMesg(mesg, adopt = true)
            225 -> SetMesg(mesg, adopt = true)
            285 -> JumpMesg(mesg, adopt = true)
            312 -> SplitMesg(mesg, adopt = true)
            313 -> SplitSummaryMesg(mesg, adopt = true)
            317 -> ClimbProMesg(mesg, adopt = true)
            206 -> FieldDescriptionMesg(mesg, adopt = true)
            207 -> DeveloperDataIdMesg(mesg, adopt = true)
            31 -> CourseMesg(mesg, adopt = true)
            32 -> CoursePointMesg(mesg, adopt = true)
            148 -> SegmentIdMesg(mesg, adopt = true)
            149 -> SegmentLeaderboardEntryMesg(mesg, adopt = true)
            150 -> SegmentPointMesg(mesg, adopt = true)
            142 -> SegmentLapMesg(mesg, adopt = true)
            151 -> SegmentFileMesg(mesg, adopt = true)
            26 -> WorkoutMesg(mesg, adopt = true)
            158 -> WorkoutSessionMesg(mesg, adopt = true)
            27 -> WorkoutStepMesg(mesg, adopt = true)
            264 -> ExerciseTitleMesg(mesg, adopt = true)
            28 -> ScheduleMesg(mesg, adopt = true)
            33 -> TotalsMesg(mesg, adopt = true)
            30 -> WeightScaleMesg(mesg, adopt = true)
            51 -> BloodPressureMesg(mesg, adopt = true)
            103 -> MonitoringInfoMesg(mesg, adopt = true)
            55 -> MonitoringMesg(mesg, adopt = true)
            211 -> MonitoringHrDataMesg(mesg, adopt = true)
            269 -> Spo2DataMesg(mesg, adopt = true)
            132 -> HrMesg(mesg, adopt = true)
            227 -> StressLevelMesg(mesg, adopt = true)
            229 -> MaxMetDataMesg(mesg, adopt = true)
            314 -> HsaBodyBatteryDataMesg(mesg, adopt = true)
            315 -> HsaEventMesg(mesg, adopt = true)
            302 -> HsaAccelerometerDataMesg(mesg, adopt = true)
            376 -> HsaGyroscopeDataMesg(mesg, adopt = true)
            304 -> HsaStepDataMesg(mesg, adopt = true)
            305 -> HsaSpo2DataMesg(mesg, adopt = true)
            306 -> HsaStressDataMesg(mesg, adopt = true)
            307 -> HsaRespirationDataMesg(mesg, adopt = true)
            308 -> HsaHeartRateDataMesg(mesg, adopt = true)
            389 -> HsaConfigurationDataMesg(mesg, adopt = true)
            409 -> HsaWristTemperatureDataMesg(mesg, adopt = true)
            145 -> MemoGlobMesg(mesg, adopt = true)
            275 -> SleepLevelMesg(mesg, adopt = true)
            82 -> AntChannelIdMesg(mesg, adopt = true)
            80 -> AntRxMesg(mesg, adopt = true)
            81 -> AntTxMesg(mesg, adopt = true)
            200 -> ExdScreenConfigurationMesg(mesg, adopt = true)
            201 -> ExdDataFieldConfigurationMesg(mesg, adopt = true)
            202 -> ExdDataConceptConfigurationMesg(mesg, adopt = true)
            268 -> DiveSummaryMesg(mesg, adopt = true)
            289 -> AadAccelFeaturesMesg(mesg, adopt = true)
            78 -> HrvMesg(mesg, adopt = true)
            290 -> BeatIntervalsMesg(mesg, adopt = true)
            370 -> HrvStatusSummaryMesg(mesg, adopt = true)
            371 -> HrvValueMesg(mesg, adopt = true)
            372 -> RawBbiMesg(mesg, adopt = true)
            297 -> RespirationRateMesg(mesg, adopt = true)
            387 -> ChronoShotSessionMesg(mesg, adopt = true)
            388 -> ChronoShotDataMesg(mesg, adopt = true)
            319 -> TankUpdateMesg(mesg, adopt = true)
            323 -> TankSummaryMesg(mesg, adopt = true)
            346 -> SleepAssessmentMesg(mesg, adopt = true)
            470 -> SleepDisruptionSeverityPeriodMesg(mesg, adopt = true)
            471 -> SleepDisruptionOvernightSeverityMesg(mesg, adopt = true)
            412 -> NapEventMesg(mesg, adopt = true)
            398 -> SkinTempOvernightMesg(mesg, adopt = true)
            105 -> PadMesg(mesg, adopt = true)
            else -> mesg
        }
}
